import React from "react";

import type {
  PlanAccount,
  SubscriptionFamily,
  UsageValue,
} from "../../lib/types";
import {
  Card,
  Grid,
  LimitKey,
  LimitMeter,
  Pill,
  SectionHead,
  SegmentBar,
  Select,
  Skeleton,
} from "../../design";
import { humanize } from "../../lib/format";
import { pct, timeOf, usdShort } from "./util";
import {
  API_BILL_TIP,
  OTHER_TIP,
  STRETCH_TIP,
  planName,
  planWindows,
  resetLabel,
  shownFamilies,
  stretchLabel,
  windowLabel,
  lastReported,
  readingLabel,
} from "./value";

function accountLabel(a: PlanAccount): string {
  return `Account ${a.id.slice(0, 4).toUpperCase()}`;
}

function accountHint(a: PlanAccount): string {
  const week = a.windows?.find((w) => w.minutes === 10080)?.used;
  return [
    `Last used ${timeOf(a.last_used_at)}`,
    week != null && `Last reported ${Math.round(week * 100)}% of weekly limit`,
  ]
    .filter(Boolean)
    .join(" · ");
}

function PlanCard({ plan }: { plan: SubscriptionFamily }) {
  const name = planName(plan.family);
  const accounts = plan.accounts ?? [];
  const [pick, setPick] = React.useState(accounts[0]?.id ?? "");
  const current = accounts.find((a) => a.id === pick) ?? accounts[0];
  const shown = accounts.length > 1 ? current : plan;
  const more = stretchLabel(shown.stretch);
  const windows = planWindows({ ...shown, family: plan.family });
  const [now, setNow] = React.useState(Date.now);
  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  const scale = Math.max(
    1,
    ...windows.flatMap((w) => [w.used ?? 0, w.without_condense ?? 0]),
  );
  return (
    <Card
      title={`Your ${name} plan`}
      info={STRETCH_TIP}
      className="per-value"
      actions={
        <>
          {accounts.length > 1 && (
            <Select
              size="sm"
              ariaLabel={`${name} account`}
              value={current.id}
              options={accounts.map((a) => ({
                value: a.id,
                label: accountLabel(a),
                hint: accountHint(a),
              }))}
              onChange={setPick}
            />
          )}
          {more && (
            <Pill tone="ok" icon="trend-up">
              {more} more work per window
            </Pill>
          )}
        </>
      }
    >
      <div className="per-stretch">
        <SegmentBar
          label={`Your ${name} usage`}
          segments={[
            {
              key: "sent",
              label: `Sent to ${name}`,
              value: shown.tokens_sent,
              amount: `${humanize(shown.tokens_sent)} tokens`,
              tone: "spent",
            },
            {
              key: "kept",
              label: "Kept off by condense",
              value: shown.tokens_kept_off,
              amount: `${humanize(shown.tokens_kept_off)} tokens`,
              tone: "saved",
            },
          ]}
        />
        {windows.length > 0 && (
          <>
            {windows.map((w) => (
              <LimitMeter
                key={w.minutes}
                label={windowLabel(w.minutes)}
                used={w.used}
                without={w.without_condense}
                scale={scale}
                resets={resetLabel(w.resets_at, now)}
                lastReported={lastReported(w, now)}
                reading={readingLabel(w, now)}
              />
            ))}
            <LimitKey />
          </>
        )}
      </div>
    </Card>
  );
}

function BillCard({ api }: { api: NonNullable<UsageValue["api"]> }) {
  const billed = Number(api.billed_usd);
  const saved = Math.max(0, Number(api.saved_usd));
  const fee = Math.max(0, Number(api.condense_fee_usd));
  const kept = Math.max(0, saved - fee);
  return (
    <Card
      title="Your API input spend"
      info={API_BILL_TIP}
      className="per-value"
      actions={
        saved > fee &&
        billed + saved > 0 && (
          <Pill tone="ok" icon="trend-down">
            {pct(kept / (billed + saved))} saved after fees
          </Pill>
        )
      }
    >
      <div className="per-stretch">
        <SegmentBar
          label="Your API bill"
          segments={[
            {
              key: "billed",
              label: "Input spend at your provider",
              value: billed,
              amount: usdShort(billed),
              tone: "spent",
            },
            ...(fee > 0
              ? [
                  {
                    key: "fee",
                    label: "condense fee",
                    value: Math.min(fee, saved),
                    amount: usdShort(fee),
                    tone: "fee" as const,
                  },
                ]
              : []),
            {
              key: "kept",
              label: "You kept",
              value: kept,
              amount: usdShort(kept),
              tone: "saved",
            },
          ]}
        />
        {(saved === 0 || fee > saved) && (
          <p className="per-stretch-note">
            {saved === 0
              ? `Nothing saved on ${usdShort(billed)} of per-token traffic yet`
              : `condense's fee was more than it saved this period`}
          </p>
        )}
      </div>
    </Card>
  );
}

function OtherCard({ other }: { other: NonNullable<UsageValue["other"]> }) {
  return (
    <Card title="Other traffic" info={OTHER_TIP} className="per-value">
      <div className="per-stretch">
        <SegmentBar
          label="Other traffic"
          segments={[
            {
              key: "sent",
              label: "Sent to the provider",
              value: other.tokens_sent,
              amount: `${humanize(other.tokens_sent)} tokens`,
              tone: "covered",
            },
            {
              key: "kept",
              label: "Kept off by condense",
              value: other.tokens_kept_off,
              amount: `${humanize(other.tokens_kept_off)} tokens`,
              tone: "saved",
            },
          ]}
        />
      </div>
    </Card>
  );
}

/** Per-plan stretch and the per-token bill, side by side for mixed users.
 * Hidden on error or with no traffic; the Summary never breaks over it. */
export function HowCondenseHelped({
  value,
  loading,
}: {
  value: UsageValue | null;
  loading: boolean;
}) {
  if (!value) {
    if (!loading) return null;
    return (
      <div className="per-value-block">
        <SectionHead title="How condense helped" />
        <Card>
          <Skeleton lines={4} />
        </Card>
      </div>
    );
  }
  const cards = [
    ...shownFamilies(value).map((f) => <PlanCard key={f.family} plan={f} />),
    ...(value.api ? [<BillCard key="api" api={value.api} />] : []),
    ...(value.other ? [<OtherCard key="other" other={value.other} />] : []),
  ];
  if (cards.length === 0) return null;
  return (
    <div className="per-value-block">
      <SectionHead title="How condense helped" />
      {cards.length === 1 ? (
        cards[0]
      ) : (
        <Grid cols={cards.length > 2 ? 3 : 2}>{cards}</Grid>
      )}
    </div>
  );
}
